import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/api';
import { NOTICE_TYPE_LABELS } from '../../lib/constants';
import type { Page, PublicNotice } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

function noticeTitle(n: PublicNotice): string {
  const title = n.payload?.title;
  return typeof title === 'string' ? title : `${n.type} notice — ${n.sourceModule}`;
}
function noticeSummary(n: PublicNotice): string | null {
  const s = n.payload?.summary;
  return typeof s === 'string' ? s : null;
}

export function Notices() {
  const [type, setType] = useState('');
  const [sourceModule, setSourceModule] = useState('');

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (type) p.set('type', type);
    if (sourceModule.trim()) p.set('sourceModule', sourceModule.trim());
    return `/api/v1/opengov/notices?${p}`;
  }, [type, sourceModule]);
  const q = useApiQuery<Page<PublicNotice>>(query);

  return (
    <div>
      <PageHeader
        title="Public Notices"
        subtitle="Gazette notices, tenders, planning applications and licences published across government."
        actions={
          <a className="btn-secondary" href="/api/v1/opengov/notices/feed.rss">
            RSS feed
          </a>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="field-label">Type</span>
          <select className="field-input min-w-[180px]" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {Object.entries(NOTICE_TYPE_LABELS).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Source module</span>
          <input
            className="field-input min-w-[200px]"
            value={sourceModule}
            onChange={(e) => setSourceModule(e.target.value)}
            placeholder="e.g. procurement-portal"
          />
        </label>
      </div>

      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? (
        <Spinner />
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No public notices match these filters" />
      ) : (
        <div className="card divide-y divide-slate-100">
          {q.data!.items.map((n) => (
            <Link key={n.id} to={`/notices/${n.id}`} className="flex items-start justify-between gap-4 px-4 py-3.5 hover:bg-slate-50">
              <div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={n.type} />
                  <span className="text-xs text-slate-400">{n.sourceModule}</span>
                </div>
                <div className="mt-1 font-semibold text-slate-900">{noticeTitle(n)}</div>
                {noticeSummary(n) ? <div className="mt-0.5 line-clamp-1 text-sm text-slate-500">{noticeSummary(n)}</div> : null}
              </div>
              <div className="shrink-0 text-xs text-slate-400">{formatDate(n.publishedAt)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
